// src/context/ThemeContext.tsx
// Which palette the app is wearing, and the one place that changes it.
//
// The context deliberately knows nothing about colours. It stores a ThemeMode --
// a name -- and the styles/theme module turns that name into a palette. Swapping
// the tokens, adding a theme, or retiring one is therefore a change to
// styles/theme.ts alone, and this file never needs to learn about it.
//
// It is kept deliberately small for the same reason QueryModalContext and
// ContactModalContext are: context + provider + a hook that throws outside the
// provider is one way of sharing app-level state, and the codebase should not
// grow a second idiom.
//
// Three details that matter for a theme provider:
//
//   - THE MODE IS READ LAZILY, DURING useState INITIALISATION, not in an effect.
//     An effect runs after the first paint, so the site would flash the default
//     palette and then repaint the stored one. Reading storage in the
//     initialiser means the first frame is already correct.
//   - STORAGE IS OPTIONAL AND FAILABLE. localStorage throws in some privacy modes
//     and does not exist at all under SSR, so every access is guarded and the app
//     silently falls back to the default rather than failing to mount.
//   - THE STORED STRING IS NOT TRUSTED. It is validated with isThemeMode, so a
//     hand-edited or stale value cannot resolve to a theme that does not exist.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { DEFAULT_THEME_MODE, isThemeMode, type ThemeMode } from '../styles/theme';

interface ThemeContextType {
  /** The active mode name, not a palette. Resolve it with getTheme(). */
  theme: ThemeMode;
  /** Switches to the other mode and remembers the choice. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/** Single place the preference is written, so the key cannot drift. */
const STORAGE_KEY = 'theme';

const readStoredMode = (): ThemeMode => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isThemeMode(stored) ? stored : DEFAULT_THEME_MODE;
  } catch {
    return DEFAULT_THEME_MODE;
  }
};

export const ThemeProviderContext = ({ children }: { children: ReactNode }) => {
  // Dark unless localStorage already says otherwise. See the note above on why
  // this is not an effect.
  const [theme, setTheme] = useState<ThemeMode>(readStoredMode);

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const nextTheme: ThemeMode = current === 'light' ? 'dark' : 'light';

      try {
        window.localStorage.setItem(STORAGE_KEY, nextTheme);
      } catch {
        // A failed write only costs the preference, never the switch.
      }

      return nextTheme;
    });
  }, []);

  // Stable identity, so consumers are not re-rendered on every provider render.
  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// Custom hook
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProviderContext');
  return context;
};
